/**
 * POS-I119: line (bargaining) discount maths for the till, a mirror of the server owner
 * `computeLineDiscountAmount` (backend/src/modules/sale/sale-totals.ts). Keep the two in step:
 *
 *  - `FIXED` is a money amount off the WHOLE line (not per unit),
 *  - `PERCENTAGE` is a share of the line value (capped at 100),
 *  - the result is rounded to 2 dp, never above the line value, never negative.
 *
 * The server re-prices every sale, so this only drives what the cashier sees while ringing up.
 */

export type LineDiscountType = 'PERCENTAGE' | 'FIXED';

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

export function computeLineDiscountAmount(
  grossAmount: number,
  type: LineDiscountType | null | undefined,
  value: number | null | undefined,
): number {
  const v = Number(value ?? 0);
  if (!type || !Number.isFinite(v) || v <= 0 || !(grossAmount > 0)) return 0;
  const raw = type === 'PERCENTAGE' ? (grossAmount * Math.min(v, 100)) / 100 : v;
  return Math.min(round2(raw), round2(grossAmount));
}

export type LineDiscountCheck = 'ok' | 'empty' | 'overLine' | 'overPercent';

/** Validates what the cashier typed before it is applied (the server caps as well). */
export function checkLineDiscountInput(
  grossAmount: number,
  type: LineDiscountType,
  value: number,
): LineDiscountCheck {
  if (!Number.isFinite(value) || value <= 0) return 'empty';
  if (type === 'PERCENTAGE') return value > 100 ? 'overPercent' : 'ok';
  return round2(value) > round2(grossAmount) ? 'overLine' : 'ok';
}

/** The discount as a share of the line value, for the manager-approval threshold. */
export function lineDiscountPercent(grossAmount: number, discountAmount: number): number {
  return grossAmount > 0 ? (discountAmount / grossAmount) * 100 : 0;
}
